import React from 'react';
import {ACTIVITY_CHANGE_EVENT} from '../lib/activityApi.js';
import {fmtCentralDateTime} from '../lib/dateUtils.js';
import {deriveRecordCreationMeta} from '../activity/recordCreationData.js';

// "No caller-supplied filter", hoisted so the default carries the prop's REAL
// type instead of the literal `null`. An inline `eventFilter = null` default
// makes TypeScript infer the prop as exactly `null`, and then no caller can
// pass the predicate this component plainly calls three lines down — the same
// defect that `loadMentionable = undefined` caused in RecordCollaborationSection.
// Value and behaviour are unchanged: the default is still null.
/** @type {((event: unknown) => boolean) | null} */
const NO_EVENT_FILTER = null;

type ActivityEventRow = {
  id: string;
  event_type: string;
  actor_display_name?: string | null;
  actor_profile_id?: string | null;
  deleted_at?: string | null;
  created_at: string;
  body?: string | null;
};

type RecordActivityLogProps = {
  entityType?: string | null;
  entityId?: string | null;
  eventFilter?: ((event: unknown) => boolean) | null;
  events: unknown[];
  refreshActivity: () => Promise<unknown>;
  fullYearDates?: boolean;
  recordCreatedAt?: string | null;
  recordCreatedBy?: string | null;
  recordCreatedByName?: string | null;
  fallbackDate?: string | null;
  creationLoading?: boolean;
  initialExpanded?: boolean;
};

export default function RecordActivityLog({
  entityType,
  entityId,
  eventFilter = NO_EVENT_FILTER,
  events,
  refreshActivity,
  fullYearDates = false,
  recordCreatedAt,
  recordCreatedBy,
  recordCreatedByName,
  fallbackDate,
  creationLoading = false,
  initialExpanded = false,
}: RecordActivityLogProps) {
  const [expanded, setExpanded] = React.useState(initialExpanded);
  const filterEvents = React.useCallback(
    (rows: unknown[]) => {
      const auditOnly = ((rows || []) as ActivityEventRow[]).filter((e) => e.event_type !== 'comment.posted');
      if (typeof eventFilter !== 'function') return auditOnly;
      return auditOnly.filter((e) => {
        try {
          return eventFilter(e);
        } catch (_e) {
          return false;
        }
      });
    },
    [eventFilter],
  );

  const filteredEvents = React.useMemo(() => filterEvents(events), [events, filterEvents]);
  const count = filteredEvents.filter((e) => !e.deleted_at).length;

  const creationFact = React.useMemo(
    () =>
      deriveRecordCreationMeta({
        recordCreatedAt,
        recordCreatedBy,
        recordCreatedByName,
        fallbackDate,
        events: filteredEvents,
      }),
    [recordCreatedAt, recordCreatedBy, recordCreatedByName, fallbackDate, filteredEvents],
  );

  React.useEffect(() => {
    function onActivityChange() {
      if (!expanded) return;
      void refreshActivity();
    }
    window.addEventListener(ACTIVITY_CHANGE_EVENT, onActivityChange);
    return () => window.removeEventListener(ACTIVITY_CHANGE_EVENT, onActivityChange);
  }, [expanded, refreshActivity]);

  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    // The mount query supplies the collapsed count. Opening deliberately asks
    // for a fresh audit list, preserving the old collapsed->expanded refresh
    // contract while Query deduplicates a still-in-flight count request.
    if (next) void refreshActivity();
  }

  if (!entityType || !entityId) return null;

  return (
    <div>
      <button
        type="button"
        data-activity-log-toggle="1"
        onClick={toggleExpanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'white',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '10px 14px',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--ink)',
          fontFamily: 'inherit',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <span style={{fontSize: 10}}>{expanded ? '▼' : '▶'}</span>
        Activity log ({count})
      </button>
      {expanded && (
        <div
          data-activity-audit-log="1"
          style={{
            background: 'white',
            border: '1px solid var(--border)',
            borderTop: 'none',
            borderRadius: '0 0 10px 10px',
            padding: 14,
          }}
        >
          <div
            data-activity-record-created="1"
            style={{
              padding: '8px 12px',
              marginBottom: 12,
              background: 'var(--bg-subtle, #f8fafc)',
              border: '1px solid var(--border, #e2e8f0)',
              borderRadius: 10,
              fontSize: 12,
              color: 'var(--ink-muted, #64748b)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <span style={{fontWeight: 600, color: 'var(--ink, #0f172a)'}}>{creationFact.factLabel}</span>
            {creationLoading ? (
              <span>loading…</span>
            ) : creationFact.effectiveDate ? (
              <>
                <span style={{color: 'var(--ink, #0f172a)'}}>
                  {fmtCentralDateTime(creationFact.effectiveDate, {fullYear: fullYearDates})}
                </span>
                {creationFact.isDerived ? (
                  <span style={{fontSize: 11, color: 'var(--ink-muted, #64748b)'}}>
                    (derived from earliest activity)
                  </span>
                ) : creationFact.createdByName ? (
                  <>
                    <span>·</span>
                    <span>by {creationFact.createdByName}</span>
                  </>
                ) : creationFact.createdBy ? (
                  <>
                    <span>·</span>
                    <span>by {creationFact.createdBy}</span>
                  </>
                ) : null}
              </>
            ) : (
              <span>date not recorded</span>
            )}
          </div>
          {filteredEvents.length === 0 && (
            <div style={{fontSize: 13, color: 'var(--ink-muted)'}}>No audit events yet.</div>
          )}
          {filteredEvents.map((ev) => (
            <div key={ev.id} style={{padding: '6px 0', borderBottom: '1px solid var(--divider)', fontSize: 12}}>
              <span style={{fontWeight: 700, color: 'var(--ink)', fontSize: 13}}>
                {ev.actor_display_name || (ev.actor_profile_id ? 'Unknown user' : 'System')}
              </span>{' '}
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: '#e0f2fe',
                  color: '#075985',
                }}
              >
                {ev.event_type.replace('.', ' ')}
              </span>
              {' · '}
              <span style={{color: 'var(--ink-muted)'}}>
                {(() => {
                  if (fullYearDates) return fmtCentralDateTime(ev.created_at, {fullYear: true});
                  try {
                    const d = new Date(ev.created_at);
                    const diff = (Date.now() - d.getTime()) / 1000;
                    if (diff < 60) return 'just now';
                    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
                    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
                    return d.toLocaleString();
                  } catch (_e) {
                    return ev.created_at;
                  }
                })()}
              </span>
              {ev.body && <div style={{color: 'var(--ink)', marginTop: 2}}>{ev.body}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
